/**
 * The pipeline monitor's rules (pure, no React): the Monitor tab
 * (PipelineMonitor.jsx) reads one projection from getPipelineMonitor
 * (functions/src/services/pipelineMonitor.ts) and works out here each
 * capture's stage, the counts and the alerts, against the server's clock.
 *
 * Event pipeline design 13, section 8. Read only: every alert names the tab
 * where its action lives.
 */

const MIN = 60 * 1000;
const HOUR = 60 * MIN;

/** How long a capture may sit in a stage before the monitor says so. */
export const THRESHOLDS = {
  uploadingMs: 30 * MIN,
  // Only while processing is on for the job (the queue running and the job switched on).
  queuedMs: 2 * HOUR,
  processingMs: 30 * MIN,
  reviewMs: 24 * HOUR,
};
/** A computer is online when it reported in the last 3 minutes (the Machines page's rule). */
export const ONLINE_MS = 3 * MIN;
/** How many SNs an alert lists before "and N more". */
export const ALERT_SNS = 5;

/** Where each action lives: the tab ids of the Rigs console (RigV2App.jsx), and Traffic Control's Machines. */
export const TAB_LABEL = {
  rig: "Rig", captures: "Captures", review: "Review", unsent: "Never emailed", originals: "Originals",
  processing: "Processing", machines: "Machines (Traffic Control)",
};

/** The stages, in the pipeline's order; each with its words and the tab where its actions are. */
export const STAGES = [
  { id: "capturing", label: "Being captured", tab: "rig" },
  { id: "captured", label: "Captured, not uploaded", tab: "captures" },
  { id: "uploading", label: "Uploading", tab: "captures" },
  { id: "uploaded", label: "Uploaded", tab: "processing" },
  { id: "queued", label: "Queued", tab: "processing" },
  { id: "processing", label: "Processing", tab: "processing" },
  { id: "failed", label: "Failed or needs you", tab: "processing" },
  { id: "review", label: "Ready for review", tab: "review" },
  { id: "approved", label: "Approved, not emailed", tab: "captures" },
  { id: "photos_only", label: "Photos only, not emailed", tab: "captures" },
  { id: "emailed", label: "Emailed", tab: "captures" },
  { id: "opened", label: "Page opened", tab: "captures" },
  { id: "downloaded", label: "Download link issued", tab: "captures" },
  { id: "not_captured", label: "Not captured", tab: "captures" },
  { id: "discarded", label: "Discarded", tab: "originals" },
  { id: "left_out", label: "Left out", tab: "review" },
  { id: "deleted", label: "Deleted", tab: "unsent" },
];
export const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]));

const has = (v) => typeof v === "number" && Number.isFinite(v);

/** The ends: nothing more happens to the capture. */
const ENDED = new Set(["discarded", "left_out", "deleted"]);
/** Ivin decided what guests get (a model approved, or photos only). */
const decided = (d) => Boolean(d) && (d.kind === "photos_only" || (d.kind === "full" && has(d.approvedAt)));
const failedTask = (t) => t?.state === "failed" || t?.state === "needs_staff";
/** The task changed after Ivin decided what guests get (or he has not decided). */
const afterDecision = (t, d) => {
  if (!decided(d)) return true;
  // His latest decision was not one about what guests get (process again, a hold): the task is current work.
  if (!has(d.decidedAt)) return true;
  const at = t?.updatedAt ?? t?.queuedAt ?? null;
  return has(at) && at > d.decidedAt;
};
/**
 * A failed task still matters unless Ivin decided about the capture after it
 * failed (photos only, say): a retry that fails after his decision is new work.
 */
const failureOpen = (row) => {
  const t = row?.task;
  if (!failedTask(t)) return false;
  const d = row?.delivery;
  if (!decided(d)) return true;
  const failedAt = t.updatedAt ?? t.error?.at ?? null;
  return !(has(d.decidedAt) && has(failedAt) && d.decidedAt >= failedAt);
};

/**
 * One capture's stage: where it is now. The ends first; then work going on
 * (a computer processing it, a model waiting for review) before what guests
 * already had, so a new model or a reprocessing is never hidden behind an
 * earlier email; then what guests got; then the queue, the upload, the rig.
 */
export function stageOf(row) {
  const up = row?.upload ?? null;
  const task = row?.task ?? null;
  const d = row?.delivery ?? null;
  const members = Array.isArray(row?.members) ? row.members : [];
  if (up?.state === "discarded") return "discarded";
  if (d?.reviewState === "left_out") return "left_out";
  if (d?.status === "deleted") return "deleted";
  if (task?.state === "cancelled" && task.reasonCode === "discarded") return "discarded";
  if (task?.state === "claimed") return "processing";
  if (failureOpen(row)) return "failed";
  // Queued again after Ivin decided (a retry, say): current work, before what guests already had.
  // A task held since before his decision (the job was never switched on, say) is not.
  if ((task?.state === "queued" || task?.state === "held") && afterDecision(task, d)) return "queued";
  // A model waits for Ivin (or he held it, or asked for it again): the Review tab.
  if (d && (has(d.candidateAt) || d.reviewState === "reprocess")) return "review";
  if (decided(d)) {
    if (members.some((m) => has(m.downloadAt))) return "downloaded";
    if (members.some((m) => has(m.openedAt))) return "opened";
    if (members.some((m) => has(m.emailedAt))) return "emailed";
    return d.kind === "photos_only" ? "photos_only" : "approved";
  }
  if (task) {
    if (task.state === "failed" || task.state === "needs_staff") return "failed";
    if (task.state === "queued" || task.state === "held") return "queued";
    if (task.state === "done") return "review";
  }
  if (up?.state === "verified") return "uploaded";
  if (up?.state === "uploading") return "uploading";
  const s = row?.attempt?.state;
  if (s === "complete") return "captured";
  if (s === "abandoned" || s === "failed") return "not_captured";
  return "capturing";
}

/** Why a task waits, stopped or was cancelled, in words, from its code (the projection carries codes, never notes). */
export const REASON_TEXT = {
  group_recipe: "waiting for the group recipe (a group capture)",
  job_not_listed: "waiting: this job is not switched on for processing",
  staff_hold: "held by staff",
  input_error: "the processing computer says these photos cannot make a model",
  tool_error: "a processing tool failed in a way a retry will not fix",
  attempts_used: "every try failed or was lost",
  no_capture_record: "no capture record for it",
  bad_upload_record: "its upload record is incomplete",
  upload_changed: "its photos changed after it was queued",
  discarded: "the capture was discarded",
  photos_gone: "its photos are gone",
  delivery_closed: "its guest delivery closed",
  staff_cancel: "cancelled by staff",
  version_deleting: "the client's raw photos are being deleted",
  other: "see the Processing tab",
};
const ERROR_TEXT = {
  input: "the photos", tool: "a processing tool", machine: "the computer", timeout: "it took too long",
  lost: "the computer stopped answering", unknown: "an unknown error",
};

/** Counts per stage (every stage present, zeros included). */
export function countsOf(rows) {
  const out = Object.fromEntries(STAGES.map((s) => [s.id, 0]));
  for (const r of rows ?? []) out[stageOf(r)] += 1;
  return out;
}

/** Processing is on for this job: the queue running and the job switched on. */
export const processingOn = (header) => Boolean(header?.switches) && header.switches.paused === false && header.switches.listed === true;

export function ago(ms) {
  if (!has(ms) || ms < 0) return "";
  const m = Math.floor(ms / MIN);
  if (m < 1) return "under a minute";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60 ? `${m % 60} min` : ""}`.trim();
  return `${Math.floor(h / 24)} days`;
}

const clock = (ms) => (has(ms) ? new Date(ms).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
}) : "");

/** A computer's name for a uid, from the header (or "a processing computer"). */
const machineName = (header, uid) =>
  (header?.computers ?? []).find((c) => c.uid === uid)?.label || "a processing computer";

/** The line under a capture: where it is, in plain words. */
export function detailOf(row, header, now) {
  const stage = stageOf(row);
  const up = row.upload;
  const task = row.task;
  const d = row.delivery;
  const members = row.members ?? [];
  const emailed = members.filter((m) => has(m.emailedAt)).length;
  const viaWords = up?.via === "uploader" ? "by the capture PC uploader" : up?.via === "staff" ? "by a staff account" : "";
  switch (stage) {
    case "capturing": return "On the rig now.";
    case "not_captured": return "The capture did not complete: nothing to upload.";
    case "captured": return `Captured ${clock(row.attempt?.completedAt ?? row.attempt?.firedAt)}; its photos are not uploaded yet.`;
    case "uploading": return `Uploading for ${ago(now - (up.startedAt ?? up.updatedAt))}${up.attempts > 1 ? ` (try ${up.attempts})` : ""}.`;
    case "uploaded":
      return [`Uploaded ${clock(up?.verifiedAt)} ${viaWords}`.trim() + ".",
        task?.state === "cancelled" ? "Processing was cancelled." : "Not in the processing queue yet."].join(" ");
    case "queued":
      if (task.state === "held") {
        const why = REASON_TEXT[task.reasonCode] ?? "";
        return why ? `${why.charAt(0).toUpperCase()}${why.slice(1)}.` : "Waiting.";
      }
      return `In the queue for ${ago(now - task.queuedAt)}${processingOn(header) ? "" : " (processing is not on for this job)"}.`;
    case "processing":
      return `On ${machineName(header, task.machineUid)}${task.stage ? `, ${task.stage}` : ""}${has(task.progress) ? ` ${Math.round(task.progress)}%` : ""}, for ${ago(now - task.claimedAt)}${task.attemptCount > 1 ? ` (try ${task.attemptCount} of ${task.attemptLimit})` : ""}.`;
    case "failed":
      return [task.state === "failed" ? "Processing failed" : "Processing needs you",
        task.reasonCode ? `: ${REASON_TEXT[task.reasonCode] ?? REASON_TEXT.other}.` : ".",
        task.error ? ` Last try: ${ERROR_TEXT[task.error.errorClass] ?? ERROR_TEXT.unknown}.` : ""].join("");
    case "review":
      if (d?.reviewState === "held") return "You held its model.";
      if (d?.reviewState === "reprocess") return "Marked to process again.";
      return `Model ready for your review for ${ago(now - (d?.candidateAt ?? task?.doneAt))}${d?.failedChecks ? `; ${d.failedChecks} check${d.failedChecks === 1 ? "" : "s"} failed` : ""}.`;
    case "approved": return `Approved ${clock(d.approvedAt)}; not emailed yet (${members.length} ${members.length === 1 ? "member" : "members"}).`;
    case "photos_only": return "Guests get the photographs alone; not emailed yet.";
    case "emailed":
    case "opened":
    case "downloaded": {
      const opened = members.filter((m) => has(m.openedAt)).length;
      const links = members.filter((m) => has(m.downloadAt)).length;
      return [`Emailed to ${emailed} of ${members.length}`, opened ? `page opened by ${opened}` : "",
        links ? `download link issued to ${links}` : ""].filter(Boolean).join(", ") + ".";
    }
    case "discarded": return "Discarded: taken out of the upload; nothing more of it is uploaded, processed or delivered.";
    case "left_out": return "Left out: no longer delivered to anyone; its files are deleted.";
    case "deleted": return "Deleted: everyone in it deleted, or their dates are over.";
    default: return "";
  }
}

const listSns = (sns) => (sns.length > ALERT_SNS ? `${sns.slice(0, ALERT_SNS).join(", ")} and ${sns.length - ALERT_SNS} more` : sns.join(", "));
const captures = (n) => `${n} ${n === 1 ? "capture" : "captures"}`;
const hasHave = (n) => (n === 1 ? "has" : "have");

/**
 * The alerts, worst first: { id, level: "error" | "warn", text, tab, sns }.
 * Grouped by kind, so 40 stuck captures make one line, not 40.
 */
export function alertsOf(rows, header, now) {
  const out = [];
  const add = (id, level, sns, text, tab) => { if (sns.length) out.push({ id, level, text: `${text} (${listSns(sns)}).`, tab, sns }); };
  const list = rows ?? [];
  const where = (fn) => list.filter(fn).map((r) => r.sn);

  add("processing_failed", "error",
    where((r) => failureOpen(r) && !ENDED.has(stageOf(r))),
    "Processing failed or needs you", "processing");
  // Only where an email can still be sent: not a capture that ended, nor a member whose page ended.
  const live = (m) => m.status !== "ended" && !m.pageEnded;
  const failedSends = where((r) => !ENDED.has(stageOf(r)) && (r.members ?? []).some((m) => live(m) && m.send === "failed"));
  add("email_failed", "error", failedSends, "An email to a guest failed", "captures");
  add("email_unconfirmed", "warn", where((r) => !failedSends.includes(r.sn) && !ENDED.has(stageOf(r)) && (r.members ?? []).some((m) => live(m) && m.send === "uncertain")),
    "An email to a guest was not confirmed by the email service", "captures");

  // Computers that stopped reporting while they held a capture.
  for (const c of header?.computers ?? []) {
    if (c.holding && (!has(c.lastSeenAt) || now - c.lastSeenAt > ONLINE_MS)) {
      const what = c.holding.thisJob && c.holding.sn ? `capture ${c.holding.sn}` : "a capture of another job";
      out.push({ id: `computer_${c.uid}`, level: "error", tab: "processing", sns: c.holding.sn ? [c.holding.sn] : [],
        text: `${c.label} stopped reporting ${ago(now - (c.lastSeenAt ?? 0))} ago while it held ${what}. If it does not come back, the capture goes back to the queue within about 6 minutes of its last report.` });
    }
  }
  // A computer that stopped answering while it processed a capture: the queue took the capture back
  // ("lost"), and the computer is still not reporting (or is unknown). One line per computer.
  const lostBy = new Map();
  for (const r of list) {
    const e = r.task?.error;
    // Claimed again by that same computer: it is back. Another computer's claim does not bring it back.
    if (!e || e.errorClass !== "lost" || (r.task.state === "claimed" && r.task.machineUid === e.machineUid) || ENDED.has(stageOf(r))) continue;
    const c = (header?.computers ?? []).find((x) => x.uid === e.machineUid) ?? null;
    if (c && has(c.lastSeenAt) && now - c.lastSeenAt <= ONLINE_MS) continue;
    if (c && has(c.lastSeenAt) && has(e.at) && c.lastSeenAt > e.at) continue;
    const k = e.machineUid ?? "?";
    lostBy.set(k, { label: c?.label ?? "A processing computer", sns: [...(lostBy.get(k)?.sns ?? []), r.sn] });
  }
  for (const [uid, v] of lostBy) {
    out.push({ id: `lost_${uid}`, level: "error", tab: "processing", sns: v.sns,
      text: `${v.label} stopped answering while it processed ${v.sns.length === 1 ? "a capture" : `${v.sns.length} captures`} and has not come back; the queue took ${v.sns.length === 1 ? "it" : "them"} back (${listSns(v.sns)}).` });
  }
  // A capture being processed whose computer stopped reporting on it (its task's own heartbeat),
  // when the computer's alert above does not already name it.
  const named = new Set(out.flatMap((a) => a.sns));
  const silentTasks = where((r) => r.task?.state === "claimed" && !named.has(r.sn)
    && now - (r.task.heartbeatAt ?? r.task.claimedAt ?? now) > ONLINE_MS);
  add("task_silent", "error", silentTasks,
    "A processing computer stopped reporting on a capture it holds; it goes back to the queue within about 6 minutes of its last report", "processing");
  const u = header?.uploader ?? null;
  if (u) {
    const silent = !has(u.reportedAt) || now - u.reportedAt > ONLINE_MS;
    if (silent && (u.pending > 0 || u.state === "running")) {
      out.push({ id: "uploader_silent", level: "error", tab: "rig", sns: [],
        text: `The capture PC uploader stopped reporting ${ago(now - (u.reportedAt ?? 0))} ago with ${u.pending} ${u.pending === 1 ? "capture" : "captures"} still to upload.` });
    }
    if (u.state === "error" || u.hasError) {
      out.push({ id: "uploader_error", level: "error", tab: "rig", sns: [],
        text: "The capture PC uploader reports a problem: its words are on the upload bar of the Rig tab, at the capture PC." });
    }
    if (u.skipped?.total > 0) {
      out.push({ id: "uploader_skipped", level: "warn", tab: "originals", sns: [],
        text: `The capture PC uploader skipped ${u.skipped.total} ${u.skipped.total === 1 ? "folder" : "folders"} for a person to look at${u.skipped.folders?.length ? ` (${u.skipped.folders.slice(0, ALERT_SNS).map((f) => `${f.folder ?? "a folder"}: ${String(f.reason).replace(/_/g, " ")}`).join("; ")})` : ""}.` });
    }
  }

  // Each judged on the record it is about, whatever the capture's stage shows (an earlier email never hides new work).
  const stuck = (ms, since) => where((r) => !ENDED.has(stageOf(r)) && has(since(r)) && now - since(r) > ms);
  const up = stuck(THRESHOLDS.uploadingMs, (r) => (r.upload?.state === "uploading" ? r.upload.startedAt ?? r.upload.updatedAt : null));
  add("stuck_uploading", "warn", up, `${captures(up.length)} ${hasHave(up.length)} been uploading for more than 30 minutes`, "captures");
  if (processingOn(header)) {
    const q = stuck(THRESHOLDS.queuedMs, (r) => (r.task?.state === "queued" ? r.task.queuedAt : null));
    add("stuck_queued", "warn", q, `${captures(q.length)} ${hasHave(q.length)} been in the queue for more than 2 hours with processing on`, "processing");
  }
  const p = stuck(THRESHOLDS.processingMs, (r) => (r.task?.state === "claimed" ? r.task.claimedAt : null));
  add("stuck_processing", "warn", p, `${captures(p.length)} ${hasHave(p.length)} been processing for more than 30 minutes`, "processing");
  // A model waiting for review (not one Ivin held), or a model reported whose record has not come yet.
  const rv = stuck(THRESHOLDS.reviewMs, (r) => {
    const d = r.delivery;
    if (d) return d.reviewState === "held" ? null : d.candidateAt ?? null;
    return r.task?.state === "done" ? r.task.doneAt : null;
  });
  add("stuck_review", "warn", rv, `${captures(rv.length)} ${hasHave(rv.length)} waited for your review for more than 24 hours`, "review");

  const rank = { error: 0, warn: 1 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}

/** A computer's line: online or not, what it is working on, last seen, version. */
export function computerLine(c, now) {
  const online = has(c.lastSeenAt) && now - c.lastSeenAt <= ONLINE_MS;
  const doing = c.holding
    ? (c.holding.thisJob && c.holding.sn
      ? `working on ${c.holding.sn}${c.holding.stage ? `, ${c.holding.stage}` : ""}${has(c.holding.progress) ? ` ${Math.round(c.holding.progress)}%` : ""}${has(c.holding.claimedAt) ? `, for ${ago(now - c.holding.claimedAt)}` : ""}`
      : "working on a capture of another job")
    : c.status === "working" ? "working" : online ? "idle" : "";
  const rest = has(c.cooldownUntil) && c.cooldownUntil > now ? `resting after three faults until ${clock(c.cooldownUntil)}` : "";
  return {
    online,
    doing,
    rest,
    seen: has(c.lastSeenAt) ? (online ? "seen just now" : `last seen ${ago(now - c.lastSeenAt)} ago`) : "never seen",
    version: c.version ? `v${c.version}${c.latestVersion && c.latestVersion !== c.version ? ` (v${c.latestVersion} available)` : ""}` : "version not reported",
  };
}

/** The uploader's line, the same way. */
export function uploaderLine(u, now) {
  if (!u) return null;
  const online = has(u.reportedAt) && now - u.reportedAt <= ONLINE_MS;
  const pct = u.current && u.current.bytesTotal > 0 ? ` ${Math.round((100 * u.current.bytesDone) / u.current.bytesTotal)}%` : "";
  const doing = u.current ? `uploading ${u.current.sn}${pct}` : u.state === "paused" ? "paused" : u.state === "idle" ? "idle" : u.state || "";
  return {
    online,
    doing,
    queue: `${u.doneCount} of ${u.totalCount} done, ${u.pending} waiting${u.pauseRequested ? ", pause asked" : ""}`,
    seen: has(u.reportedAt) ? (online ? "seen just now" : `last seen ${ago(now - u.reportedAt)} ago`) : "never seen",
    version: "version not reported",
  };
}

/** Rows that match a stage filter ("all", "alerts" or a stage id) and a search (SN, number or initials). */
export function filterRows(rows, { stage = "all", query = "", alertSns = null } = {}) {
  const q = String(query).trim().toLowerCase();
  return (rows ?? []).filter((r) => {
    if (stage === "alerts") { if (!alertSns?.has(r.sn)) return false; } else if (stage !== "all" && stageOf(r) !== stage) return false;
    if (!q) return true;
    if (r.sn.toLowerCase().includes(q)) return true;
    return (r.members ?? []).some((m) => (m.no && String(m.no).toLowerCase().replace(/^#/, "") === q.replace(/^#/, "")) || (m.name && m.name.toLowerCase().replace(/[.\s]/g, "") === q.replace(/[.\s]/g, "")));
  });
}
